import { useEffect } from 'react'

import { SearchContextState } from '@elastic/react-search-ui/lib/cjs/withSearch'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { createParam } from 'solito'
import { useRouter } from 'solito/router'

import useModal from 'app/hooks/useModal'
import useSubCategoryOptions from 'app/hooks/useSubCategoryOptions'
import Alert from 'app/services/Alert'
import ErrorHandler, { isErrorHandlerType } from 'app/services/ErrorHandler'
import {
  useCreateMatchMeMutation,
  useCreateUnauthMatchMeMutation
} from 'app/services/Query/match-me/match-me.mutation'
import useAuthStore, { getIsConfirmed } from 'app/store/auth.store'
import { SEARCH_LINK } from 'app/utils/constants/menu.constants'
import { clearFieldValueIfNotFound } from 'app/utils/helpers/form.helpers'
import { MatchMeSchema } from 'app/validation/schemas/user.schemas'

import {
  canAddMatchMe,
  generateLetsMatchedFormValues
} from '../helpers/match-me.helpers'

const { useParam } = createParam()

const defaultFormValues = {
  part_number: '',
  makes: '',
  models: '',
  generation_id: '',
  category_id: '',
  sub_category_id: '',
  prefilling: true
}

export const useLetsMatchedForm = (filters: SearchContextState['filters']) => {
  const { formatMessage } = useIntl()
  const { open, showModal, hideModal } = useModal()
  const isAuth = useAuthStore(getIsConfirmed)
  const router = useRouter()
  const [isPartRequest = ''] = useParam('partRequest')

  const methods = useForm<MatchMeFV>({
    resolver: yupResolver(MatchMeSchema),
    defaultValues: defaultFormValues,
    context: { isAuth }
  })
  const { mutateAsync: mutateAuthMatchMe } = useCreateMatchMeMutation()
  const { mutateAsync: mutateUnauthMatchMe } = useCreateUnauthMatchMeMutation()

  const make = useWatch({ control: methods.control, name: 'makes' })
  const model = useWatch({ control: methods.control, name: 'models' })
  const generation = useWatch({
    control: methods.control,
    name: 'generation_id'
  })
  const prefilling = useWatch({
    control: methods.control,
    name: 'prefilling'
  })
  const [categoryId, subCategoryId] = useWatch({
    control: methods.control,
    name: ['category_id', 'sub_category_id']
  })

  const { subCategoryOptions } = useSubCategoryOptions({
    categoryId,
    loadResponse: (res) => {
      clearFieldValueIfNotFound({
        name: 'sub_category_id',
        value: '',
        data: res,
        findKey: 'id',
        findValue: subCategoryId,
        setValue: methods.setValue
      })
    }
  })

  useEffect(() => {
    if (!model || prefilling) return

    methods.setValue('models', '')
  }, [make])

  useEffect(() => {
    if (!generation || prefilling) return

    methods.setValue('generation_id', '')
  }, [model])

  useEffect(() => {
    if (!subCategoryId || prefilling) return

    methods.setValue('sub_category_id', '')
  }, [categoryId])

  //prefill form values
  useEffect(() => {
    if (!prefilling) return

    const filtersToFormValues = generateLetsMatchedFormValues({
      filters,
      defaultFormValues
    })

    methods.reset(filtersToFormValues)

    setTimeout(() => methods.setValue('prefilling', false), 200)
  }, [filters])

  const onSubmit = async (values: MatchMeFV) => {
    try {
      const { prefilling: _prefilling, ...matchValues } = values

      if (isAuth) {
        await mutateAuthMatchMe(matchValues)
      } else {
        await mutateUnauthMatchMe(matchValues)
      }

      Alert.success(formatMessage({ defaultMessage: 'Successfully added' }))

      if (open) hideModal()

      methods.reset({ ...values, email: '' })

      if (isPartRequest) router.push(SEARCH_LINK)
    } catch (error) {
      console.error(error)

      if (isErrorHandlerType(error)) {
        const customError = 'message' in error ? error.message : ''

        new ErrorHandler(error.data || customError)
      }
    }
  }

  const isMakeDisabled = !make
  const isModelDisabled = !model
  const isGenerationDisabled = isMakeDisabled || isModelDisabled

  const { isSubmitting, isDirty, isSubmitted } = methods.formState
  const modalSubmit = open
    ? () => methods.handleSubmit(onSubmit)()
    : () => showModal()
  const handleSubmit = isAuth
    ? () => methods.handleSubmit(onSubmit)()
    : modalSubmit

  const isActiveButton = isSubmitted
    ? isDirty && canAddMatchMe(methods.getValues())
    : canAddMatchMe(methods.getValues())

  return {
    methods,
    make,
    model,
    categoryId,
    isGenerationDisabled,
    subCategoryOptions,
    handleSubmit,
    isSubmitting,
    open,
    hideModal,
    isActiveButton
  }
}
